/* ==========================================================================
   FNDRY — Checkout newsletter offer (F2c 2026-09-28)

   Pressing Checkout (the cart drawer or /cart) opens a small dialog once per
   browser: join the newsletter for an extra 10% off this order.

   - The X, "No thanks", Escape and a tap outside go straight to checkout.
   - Joining submits the store's own customer form (tag: newsletter). That form
     is guarded by Shopify's spam protection (hCaptcha), which owns the actual
     post: it runs its check, then posts the form natively. The post is pointed
     at a hidden frame so this page stays put; once the store has answered, she
     goes to /discount/CODE?redirect=/checkout, which applies the code on the
     way to checkout. While that runs the dialog steps out of the top layer
     (modal to non-modal), so a hCaptcha puzzle, if one is shown, sits on top
     of it and can be answered.
   - It never shows when: the section setting is off, the cart already has a
     discount code, a signed-in customer already subscribes, the browser has
     seen it before, or the browser's storage can't record that it has.
   - It must never trap or delay checkout. Anything unexpected before the
     dialog opens lets the original submit carry on; anything after it opens
     sends her to checkout. A join that hasn't finished in 20 seconds goes to
     checkout with the code anyway, and pressing Checkout again does the same
     at once.

   The markup lives in sections/fndry-cart-drawer.liquid, outside the drawer
   root. localStorage is used here on Nema's instruction (2026-09-28), always
   inside try/catch, and only to remember that the dialog was seen.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__fndryCheckoutOffer) return;
  window.__fndryCheckoutOffer = true;

  var KEY = 'fndry:checkout-offer';
  var SINK = 'fndry-offer-sink';
  var JOIN_TIMEOUT = 20000;
  var leaving = false;
  var pendingUrl = null; /* set while a join is on its way to checkout */
  var timers = [];

  function dialog() {
    return document.querySelector('[data-fndry-checkout-offer]');
  }

  /* A usable storage, or null. A browser that can't write it never sees the
     dialog, so "once per browser" can't turn into "every time". */
  function storage() {
    try {
      var s = window.localStorage;
      var probe = KEY + ':probe';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return s;
    } catch (e) {
      return null;
    }
  }

  function remember(value) {
    try {
      var s = storage();
      if (s) s.setItem(KEY, value);
    } catch (e) {}
  }

  function hasDiscountCode() {
    var content = document.querySelector('[data-fcd-content]');
    if (content && content.getAttribute('data-discount-code') === 'true') return true;
    return /(?:^|;\s*)discount_code=[^;\s]/.test(document.cookie || '');
  }

  function shouldShow(d) {
    if (!d || d.getAttribute('data-enabled') !== 'true') return false;
    if (typeof d.showModal !== 'function') return false;
    if (window.Shopify && window.Shopify.designMode) return false;
    if (d.getAttribute('data-subscribed') === 'true') return false;
    if (!d.getAttribute('data-code')) return false;
    var s = storage();
    if (!s || s.getItem(KEY)) return false;
    if (hasDiscountCode()) return false;
    return true;
  }

  function checkoutUrl(d) {
    return (d && d.getAttribute('data-checkout-url')) || '/checkout';
  }

  function discountUrl(d) {
    var code = (d && d.getAttribute('data-code')) || '';
    return code ? '/discount/' + encodeURIComponent(code) + '?redirect=' + checkoutUrl(d) : checkoutUrl(d);
  }

  function go(url) {
    if (leaving) return;
    leaving = true;
    window.location.assign(url);
  }

  function toCheckout(d) {
    remember('dismissed');
    go(checkoutUrl(d));
  }

  function later(fn, ms) {
    timers.push(window.setTimeout(fn, ms));
  }

  /* ---------- Checkout pressed: open the dialog instead, once ------------- */

  document.addEventListener(
    'submit',
    function (event) {
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;

      /* She has already joined and the code is on its way: go with it now. */
      if (pendingUrl) {
        event.preventDefault();
        go(pendingUrl);
        return;
      }

      var d;
      try {
        d = dialog();
        if (!shouldShow(d)) return; /* the original submit carries on */
      } catch (e) {
        return;
      }

      event.preventDefault();
      try {
        remember('shown');
        d.showModal();
      } catch (e) {
        toCheckout(d);
      }
    },
    true
  );

  /* ---------- Inside the dialog ------------------------------------------ */

  document.addEventListener('click', function (event) {
    var d = dialog();
    if (!d || !d.open) return;

    try {
      if (event.target.closest('[data-offer-close]') || event.target.closest('[data-offer-skip]')) {
        event.preventDefault();
        if (pendingUrl) go(pendingUrl); /* already joined: keep the code */
        else toCheckout(d);
        return;
      }

      /* a tap on the backdrop lands on the dialog element itself */
      if (event.target === d && !pendingUrl) {
        var r = d.getBoundingClientRect();
        var inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
        if (!inside) toCheckout(d);
      }
    } catch (e) {
      toCheckout(d);
    }
  });

  document.addEventListener(
    'cancel',
    function (event) {
      var d = dialog();
      if (!d || event.target !== d) return;
      event.preventDefault(); /* Escape: same as the X */
      toCheckout(d);
    },
    true
  );

  /* The open cart drawer traps Tab and Escape for itself; while the dialog is
     open those keys belong to the dialog. */
  document.addEventListener(
    'keydown',
    function (event) {
      var d = dialog();
      if (!d || !d.open) return;
      if (event.key === 'Tab' || event.key === 'Escape') event.stopPropagation();
    },
    true
  );

  /* ---------- Joining ------------------------------------------------------ */

  function sink() {
    var frame = document.querySelector('iframe[name="' + SINK + '"]');
    if (frame) return frame;
    frame = document.createElement('iframe');
    frame.name = SINK;
    frame.title = '';
    frame.tabIndex = -1;
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:absolute;width:1px;height:1px;border:0;left:-9999px;top:0;visibility:hidden;';
    document.body.appendChild(frame);
    return frame;
  }

  /* True once the store has answered the post: the hidden frame has left
     about:blank for whatever page the store sent it to. */
  function answered(frame) {
    try {
      var href = frame.contentWindow.location.href;
      return !!href && href !== 'about:blank';
    } catch (e) {
      return true; /* cross-origin: it has certainly navigated */
    }
  }

  function busy(d, on) {
    var label = d.getAttribute('data-busy-label');
    document.querySelectorAll('button[name="checkout"], [data-offer-submit]').forEach(function (b) {
      if (on) {
        if (!b.hasAttribute('data-label')) b.setAttribute('data-label', b.textContent);
        b.setAttribute('aria-busy', 'true');
        if (label) b.textContent = label;
      } else {
        b.removeAttribute('aria-busy');
        if (b.hasAttribute('data-label')) b.textContent = b.getAttribute('data-label');
        b.removeAttribute('data-label');
      }
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    var d = dialog();
    if (!d || !form || !d.contains(form)) return;

    /* Shopify's spam protection has its own listener on this form, which has
       already stopped the plain submit and started its check (it posts the
       form itself when that passes). Nothing here may submit it a second time. */
    event.preventDefault();

    var next = discountUrl(d);
    pendingUrl = next;
    remember('joined');

    try {
      var frame = sink();
      form.target = SINK;
      d.close();
      d.show(); /* out of the top layer: a hCaptcha puzzle must sit above it */
      busy(d, true);
      var submitButton = form.querySelector('[data-offer-submit]');
      if (submitButton) submitButton.disabled = true;

      var protectedByShopify = !!(window.Shopify && window.Shopify.captcha);
      if (!protectedByShopify) {
        HTMLFormElement.prototype.submit.call(form); /* no spam protection on this store: post it now */
      } else if (form.getAttribute('data-hcaptcha-bound') !== 'true') {
        /* The protection hadn't attached to the form yet, so the submit was
           only held: hand it over once it has, or post it plainly if it never does. */
        var waited = 0;
        var poll = window.setInterval(function () {
          waited += 100;
          if (form.getAttribute('data-hcaptcha-bound') === 'true') {
            window.clearInterval(poll);
            form.submit(); /* the protection's own submit: check, then post */
          } else if (waited >= 1500) {
            window.clearInterval(poll);
            HTMLFormElement.prototype.submit.call(form);
          }
        }, 100);
        timers.push(poll);
      }

      var watch = window.setInterval(function () {
        if (answered(frame)) {
          window.clearInterval(watch);
          go(next);
        }
      }, 100);
      timers.push(watch);
      later(function () { go(next); }, JOIN_TIMEOUT);
    } catch (e) {
      go(next);
    }
  });

  /* Back from checkout (page restored from the back/forward cache): nothing
     may be left open, busy or pending on a page she can see again. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    leaving = false;
    pendingUrl = null;
    timers.forEach(function (t) {
      window.clearTimeout(t);
      window.clearInterval(t);
    });
    timers = [];
    var d = dialog();
    if (!d) return;
    try {
      if (d.open) d.close();
      busy(d, false);
      var submitButton = d.querySelector('[data-offer-submit]');
      if (submitButton) submitButton.disabled = false;
      var frame = document.querySelector('iframe[name="' + SINK + '"]');
      if (frame) frame.remove();
    } catch (e) {}
  });
})();
